import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { PartnerHoursEditor, validateHours } from '../components/PartnerHoursEditor';
import { useAuth } from '../lib/auth';
import {
  listPartnersAdmin,
  createPartner,
  updatePartner,
  deletePartner,
  uploadFile,
  type Partner,
  type PartnerHour,
  type PartnerPayload,
  type Tier,
} from '../lib/admin';

const TIERS: Tier[] = ['platinum', 'gold', 'silver', 'bronze'];

// 26 Swiss cantons in canonical FSO order (ZH, BE, … JU). Hardcoded here
// rather than fetched: the list is fixed by the Confederation and small
// enough to inline. Names are i18n-able later; for the admin console the
// 2-letter code + English name is fine.
const CANTONS: Array<{ code: string; name: string }> = [
  { code: 'ZH', name: 'Zürich' },
  { code: 'BE', name: 'Bern' },
  { code: 'LU', name: 'Lucerne' },
  { code: 'UR', name: 'Uri' },
  { code: 'SZ', name: 'Schwyz' },
  { code: 'OW', name: 'Obwalden' },
  { code: 'NW', name: 'Nidwalden' },
  { code: 'GL', name: 'Glarus' },
  { code: 'ZG', name: 'Zug' },
  { code: 'FR', name: 'Fribourg' },
  { code: 'SO', name: 'Solothurn' },
  { code: 'BS', name: 'Basel-Stadt' },
  { code: 'BL', name: 'Basel-Landschaft' },
  { code: 'SH', name: 'Schaffhausen' },
  { code: 'AR', name: 'Appenzell Ausserrhoden' },
  { code: 'AI', name: 'Appenzell Innerrhoden' },
  { code: 'SG', name: 'St. Gallen' },
  { code: 'GR', name: 'Graubünden' },
  { code: 'AG', name: 'Aargau' },
  { code: 'TG', name: 'Thurgau' },
  { code: 'TI', name: 'Ticino' },
  { code: 'VD', name: 'Vaud' },
  { code: 'VS', name: 'Valais' },
  { code: 'NE', name: 'Neuchâtel' },
  { code: 'GE', name: 'Geneva' },
  { code: 'JU', name: 'Jura' },
];

export function AdminPartners() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [partners, setPartners] = useState<Partner[] | null>(null);
  const [editing, setEditing] = useState<Partner | 'new' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    if (!accessToken) return;
    try {
      const list = await listPartnersAdmin(accessToken);
      setPartners(list);
    } catch (e: any) {
      setErr(`${e?.message || e}`);
    }
  }

  useEffect(() => {
    if (accessToken) load();
  }, [accessToken]);

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Partners & sponsors</h1>
            <div>
              <button class="admin-primary" onClick={() => setEditing('new')}>+ Add partner</button>
            </div>
          </header>

          <p class="admin-lede">
            Bars, pubs and breweries donating beer to the prize pool. The
            public list is filtered to active only.
          </p>

          {err && <p class="admin-error">{err}</p>}
          {!partners && !err && <p class="admin-lede">Loading…</p>}

          {partners && partners.length === 0 && (
            <p class="admin-lede">No partners yet — add the first.</p>
          )}

          {partners && partners.length > 0 && (
            <table class="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tier</th>
                  <th>Canton</th>
                  <th>Litres</th>
                  <th>URL</th>
                  <th>Owner</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.id} style={{ opacity: p.active ? 1 : 0.5 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {p.logoPath && (
                          <img
                            src={p.logoPath}
                            alt=""
                            style={{
                              width: 28, height: 28, objectFit: 'contain',
                              background: '#0e0c0a',
                              border: '1px solid rgba(245,237,224,0.1)',
                              borderRadius: 6, padding: 2,
                            }}
                          />
                        )}
                        <div>
                          <div>{p.name}</div>
                          <div class="admin-mono">{p.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td>{p.tier}</td>
                    <td>{p.canton ?? '—'}</td>
                    <td>{p.contributionLitres ?? '—'}</td>
                    <td>
                      {p.url ? (
                        <a href={p.url} target="_blank" rel="noopener" class="admin-link">
                          {hostnameOf(p.url)} ↗
                        </a>
                      ) : '—'}
                    </td>
                    <td>
                      {p.ownerEmail ? (
                        <div>
                          <div class="admin-mono" style={{ fontSize: 11 }}>{p.ownerEmail}</div>
                          <div style={{ fontSize: 10, color: p.claimedAt ? '#7ec97e' : 'rgba(245,237,224,0.55)' }}>
                            {p.claimedAt ? `claimed ${fmtDate(p.claimedAt)}` : 'not claimed yet'}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'rgba(245,237,224,0.4)' }}>—</span>
                      )}
                    </td>
                    <td>{p.active ? 'yes' : 'no'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button class="admin-link" onClick={() => setEditing(p)}>Edit</button>
                      {' · '}
                      <a
                        class="admin-link"
                        href={localePath(`/partner?as=${p.id}`)}
                        target="_blank"
                        rel="noopener"
                        title="Open the partner self-service page as this partner"
                      >
                        View as ↗
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {editing && accessToken && (
            <PartnerModal
              partner={editing === 'new' ? null : editing}
              accessToken={accessToken}
              onClose={() => setEditing(null)}
              onSaved={async () => {
                setEditing(null);
                await load();
              }}
            />
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function PartnerModal({
  partner,
  accessToken,
  onClose,
  onSaved,
}: {
  partner: Partner | null;
  accessToken: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useI18n();
  const isNew = partner === null;
  const [name, setName] = useState(partner?.name ?? '');
  const [slug, setSlug] = useState(partner?.slug ?? '');
  const [tier, setTier] = useState<Tier>(partner?.tier ?? 'bronze');
  const [canton, setCanton] = useState(partner?.canton ?? '');
  const [url, setUrl] = useState(partner?.url ?? '');
  const [logoPath, setLogoPath] = useState(partner?.logoPath ?? '');
  const [litres, setLitres] = useState(partner?.contributionLitres?.toString() ?? '');
  const [address, setAddress] = useState(partner?.address ?? '');
  const [lat, setLat] = useState(partner?.lat?.toString() ?? '');
  const [lng, setLng] = useState(partner?.lng?.toString() ?? '');
  const [redemptionCode, setRedemptionCode] = useState(partner?.redemptionCode ?? '');
  const [remainingLitres, setRemainingLitres] = useState(partner?.remainingLitres?.toString() ?? '');
  const [notes, setNotes] = useState(partner?.notes ?? '');
  const [active, setActive] = useState(partner?.active ?? true);
  const [whiteBg, setWhiteBg] = useState(partner?.whiteBg ?? false);
  const [infoLocked, setInfoLocked] = useState(partner?.infoLocked ?? false);
  const [ownerEmail, setOwnerEmail] = useState(partner?.ownerEmail ?? '');
  const [hours, setHours] = useState<PartnerHour[]>(partner?.hours ?? []);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function onLogoFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const res = await uploadFile(accessToken, file, 'partners');
      setLogoPath(res.url);
    } catch (e: any) {
      setErr(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
      input.value = ''; // allow re-selecting the same file
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const latStr = lat.trim();
      const lngStr = lng.trim();
      if ((latStr === '') !== (lngStr === '')) {
        throw new Error('Set both lat and lng, or leave both empty');
      }
      const latNum = latStr === '' ? null : Number(latStr);
      const lngNum = lngStr === '' ? null : Number(lngStr);
      if (latNum !== null && (isNaN(latNum) || latNum < -90 || latNum > 90)) {
        throw new Error('Lat must be a number between -90 and 90');
      }
      if (lngNum !== null && (isNaN(lngNum) || lngNum < -180 || lngNum > 180)) {
        throw new Error('Lng must be a number between -180 and 180');
      }
      const remStr = remainingLitres.trim();
      let remNum: number | null = null;
      if (remStr !== '') {
        remNum = Number(remStr);
        if (isNaN(remNum) || remNum < 0) {
          throw new Error('Remaining litres must be a non-negative number');
        }
      }
      const code = redemptionCode.trim();
      if (code !== '' && !/^[A-Za-z0-9-]{3,12}$/.test(code)) {
        throw new Error('Redemption code: 3–12 chars, letters/digits/dash only');
      }
      const hv = validateHours(hours, t);
      if (!hv.ok) throw new Error(hv.error);
      const payload: PartnerPayload = {
        name: name.trim(),
        slug: slug.trim() || undefined,
        tier,
        canton: canton.trim() || undefined,
        address: address.trim() || undefined,
        lat: latNum,
        lng: lngNum,
        url: url.trim() || undefined,
        logoPath: logoPath.trim() || undefined,
        contributionLitres: litres.trim() === '' ? null : parseInt(litres, 10),
        redemptionCode: code || null,
        remainingLitres: remNum,
        notes: notes.trim() || undefined,
        active,
        whiteBg,
        infoLocked,
        ownerEmail: ownerEmail.trim(),
        hours: hv.hours,
      };
      if (isNew) {
        await createPartner(accessToken, payload);
      } else {
        await updatePartner(accessToken, partner!.id, payload);
      }
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!partner) return;
    if (!confirm(`Soft-delete "${partner.name}"? Mark inactive — can re-enable later.`)) return;
    setSaving(true);
    try {
      await deletePartner(accessToken, partner.id);
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form class="admin-modal" style={{ width: 'min(560px, 100%)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>{isNew ? 'New partner' : `Edit · ${partner!.name}`}</h2>

        <label class="admin-field">
          <span>Name *</span>
          <input value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} required />
        </label>

        <label class="admin-field">
          <span>Slug (leave blank to auto-generate from name)</span>
          <input value={slug} onInput={(e) => setSlug((e.target as HTMLInputElement).value)} placeholder="bar-vino" />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <label class="admin-field">
            <span>Tier</span>
            <select value={tier} onChange={(e) => setTier((e.target as HTMLSelectElement).value as Tier)}>
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label class="admin-field">
            <span>Canton</span>
            <select value={canton} onChange={(e) => setCanton((e.target as HTMLSelectElement).value)}>
              <option value="">— national / multi-canton</option>
              {CANTONS.map((c) => (
                <option key={c.code} value={c.code}>{c.code} · {c.name}</option>
              ))}
            </select>
          </label>
          <label class="admin-field">
            <span>Litres donated</span>
            <input type="number" min={0} value={litres} onInput={(e) => setLitres((e.target as HTMLInputElement).value)} />
          </label>
        </div>

        <label class="admin-field">
          <span>Website URL</span>
          <input value={url} type="url" onInput={(e) => setUrl((e.target as HTMLInputElement).value)} placeholder="https://example.com" />
        </label>

        <label class="admin-field">
          <span>Address (street, postcode, city)</span>
          <input value={address} onInput={(e) => setAddress((e.target as HTMLInputElement).value)} placeholder="Rue du Marché 12, 1003 Lausanne" />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <label class="admin-field">
            <span>Latitude</span>
            <input
              value={lat}
              inputMode="decimal"
              onInput={(e) => setLat((e.target as HTMLInputElement).value)}
              placeholder="46.519962"
            />
          </label>
          <label class="admin-field">
            <span>Longitude</span>
            <input
              value={lng}
              inputMode="decimal"
              onInput={(e) => setLng((e.target as HTMLInputElement).value)}
              placeholder="6.633597"
            />
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <label class="admin-field">
            <span>Redemption code (typed by partner)</span>
            <input
              value={redemptionCode}
              onInput={(e) => setRedemptionCode((e.target as HTMLInputElement).value)}
              placeholder="MOULIN-7Q"
              autocomplete="off"
              spellcheck={false}
            />
          </label>
          <label class="admin-field">
            <span>Remaining litres (live stock)</span>
            <input
              value={remainingLitres}
              inputMode="decimal"
              onInput={(e) => setRemainingLitres((e.target as HTMLInputElement).value)}
              placeholder="50.00"
            />
          </label>
        </div>

        <div class="admin-field">
          <span style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.06em', color: 'rgba(245,237,224,0.6)', fontFamily: 'IBM Plex Mono, Menlo, monospace' }}>
            Opening hours
          </span>
          <p style={{ margin: '0 0 8px', fontSize: 11, color: 'rgba(245,237,224,0.55)' }}>
            Per-day slots in Europe/Zurich. Multiple slots/day OK (lunch + dinner). For overnight,
            set the close time earlier than open — backend wraps into the next day.
          </p>
          <PartnerHoursEditor hours={hours} onChange={setHours} />
        </div>

        <div class="admin-field">
          <span style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.06em', color: 'rgba(245,237,224,0.6)', fontFamily: 'IBM Plex Mono, Menlo, monospace' }}>
            Logo
          </span>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {logoPath && (
              <img
                src={logoPath}
                alt=""
                style={{
                  width: 64, height: 64, objectFit: 'contain',
                  background: '#0e0c0a', border: '1px solid rgba(245,237,224,0.1)',
                  borderRadius: 8, padding: 4,
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                disabled={uploading}
                onChange={onLogoFile}
                style={{ display: 'block', marginBottom: 6, color: 'rgba(245,237,224,0.8)' }}
              />
              <input
                value={logoPath}
                onInput={(e) => setLogoPath((e.target as HTMLInputElement).value)}
                placeholder="https://storage.googleapis.com/beercup-uploads/partners/…"
                style={{ width: '100%', fontSize: 11, fontFamily: 'IBM Plex Mono, Menlo, monospace' }}
              />
              {uploading && <p style={{ fontSize: 12, color: 'rgba(245,237,224,0.6)', margin: '4px 0 0' }}>Uploading…</p>}
            </div>
          </div>
        </div>

        <label class="admin-field">
          <span>Notes (admin only)</span>
          <textarea rows={3} value={notes} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
        </label>

        <label class="admin-field">
          <span>Owner email (partner onboarding)</span>
          <input
            value={ownerEmail}
            type="email"
            autocomplete="off"
            spellcheck={false}
            onInput={(e) => setOwnerEmail((e.target as HTMLInputElement).value)}
            placeholder="mario@bar-vino.ch"
          />
          <span style={{ display: 'block', fontSize: 11, color: 'rgba(245,237,224,0.55)', marginTop: 4 }}>
            When this address signs in (web or app), they're auto-promoted to partner and linked to this row.
            {partner?.claimedAt && ` Claimed ${fmtDate(partner.claimedAt)}.`}
          </span>
        </label>

        <label class="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive((e.target as HTMLInputElement).checked)} />
          <span style={{ textTransform: 'none', letterSpacing: 0, color: '#f5ede0', fontFamily: 'inherit' }}>
            Active (shown publicly)
          </span>
        </label>

        <label class="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          <input type="checkbox" checked={whiteBg} onChange={(e) => setWhiteBg((e.target as HTMLInputElement).checked)} />
          <span style={{ textTransform: 'none', letterSpacing: 0, color: '#f5ede0', fontFamily: 'inherit' }}>
            White background (for dark or transparent logos)
          </span>
        </label>

        <label class="admin-field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: '8px' }}>
          <input type="checkbox" checked={infoLocked} onChange={(e) => setInfoLocked((e.target as HTMLInputElement).checked)} style={{ marginTop: 3 }} />
          <span style={{ textTransform: 'none', letterSpacing: 0, color: '#f5ede0', fontFamily: 'inherit' }}>
            Lock partner info
            <span style={{ display: 'block', fontSize: 11, color: 'rgba(245,237,224,0.55)', marginTop: 2 }}>
              Once on, the partner can't edit name, logo, website, address or map pin from /partner — they have to email us.
              Their redemption code and drink-size toggles stay editable.
            </span>
          </span>
        </label>

        {err && <p class="admin-error">{err}</p>}

        <div class="admin-modal-actions">
          {!isNew && (
            <button type="button" class="admin-link" onClick={onDelete} style={{ marginRight: 'auto', color: '#ff8b6b' }}>
              Soft delete
            </button>
          )}
          <button type="button" class="admin-link" onClick={onClose}>Cancel</button>
          <button type="submit" class="admin-primary" disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
